<template>
  <!-- prettier-ignore -->
  <div class="footer">
    <div>
      <router-link
        v-if="prev.to"
        class="footer__link"
        cy-link="prev"
        :to="prev.to"
        @click.native="scrollTop">
        <ovp-icon
          class="link__icon"
          art="light"
          name="angle-down"
          rotation="90"
        />
        <span v-html="prevLabel" />
      </router-link>
    </div>

    <div>
      <ovp-button
        v-if="next.to"
        class="footer__button"
        primary
        @click="goNext"
      >
        <span v-html="nextLabel" />
        <ovp-icon
          class="button__icon"
          art="light"
          name="angle-down"
          rotation="270"
        />
      </ovp-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import { PersonHelper, RouteHelper } from '@/helpers'
import { EVENT_SIDE_NAV } from '@/config/events'

export default {
  name: 'CategoryFooter',

  props: {
    categoryId: {
      type: String,
      required: true,
    },

    personId: {
      type: String,
      required: true,
    },
  },

  created() {
    this.$EventBus.$on(EVENT_SIDE_NAV.CLICK, this.sideNavClick)
  },

  beforeDestroy() {
    this.$EventBus.$off(EVENT_SIDE_NAV.CLICK, this.sideNavClick)
    this.$EventBus.$emit(EVENT_SIDE_NAV.UPDATE, { reset: true })
  },

  watch: {
    prev: {
      immediate: true,
      deep: true,
      handler: function (value) {
        this.$EventBus.$emit(EVENT_SIDE_NAV.UPDATE, { back: !!value && !!value.to })
      },
    },
    next: {
      immediate: true,
      deep: true,
      handler: function (value) {
        this.$EventBus.$emit(EVENT_SIDE_NAV.UPDATE, { forward: !!value && !!value.to })
      },
    },
  },

  computed: {
    ...mapGetters('basket', ['getCategories']),
    ...mapState('basket', ['persons']),

    categories() {
      // prettier-ignore
      return this.getCategories(this.personId)
        .filter(category => !category.hidden)
    },

    index() {
      return this.categories.findIndex(c => c.id === this.categoryId)
    },

    personIndex() {
      return this.persons.findIndex(p => p.personId === this.personId)
    },

    prev() {
      const person = this.persons[this.personIndex - 1]
      const category = this.categories[this.index - 1]

      return this.getRoute(person, category, this.categories[this.categories.length - 1])
    },

    prevLabel() {
      const page = this.$t('screen.products.backTo', { category: this.$t(this.prev.key) })
      const name = this.prev.name
      return name ? `${page} ${name}` : page
    },

    next() {
      const person = this.persons[this.personIndex + 1]
      const category = this.categories[this.index + 1]

      if (!category && !person) {
        const nextRoute = RouteHelper.getNextAvailableRoute(this.$route)
        if (!nextRoute) {
          return {}
        }
        return {
          key: `navigation.${nextRoute.name}`,
          to: !nextRoute.disabled ? nextRoute.path : false,
        }
      }

      return this.getRoute(person, category, this.categories[0])
    },

    nextLabel() {
      const nextPage = this.$t('screen.products.nextTo', { category: this.$t(this.next.key) })
      const nextPerson = this.next.name
      return nextPerson ? `${nextPage} ${nextPerson}` : nextPage
    },
  },

  methods: {
    getRoute(person, category, fallback) {
      if (category) {
        return {
          key: `content.categories.${category.id}.title`,
          to: `/products/${this.personId}/${category.id}`,
        }
      } else if (!category && person) {
        return {
          key: `content.categories.${fallback.id}.title`,
          to: `/products/${person.personId}/${fallback.id}`,
          name: PersonHelper.getFirstName(person.personId, this.$t.bind(this)),
        }
      } else {
        return {}
      }
    },

    goNext() {
      this.scrollTop()
      this.$router.push(this.next.to)
    },

    goPrev() {
      this.scrollTop()
      this.$router.push(this.prev.to)
    },

    scrollTop() {
      if (!this.$browser.isDesktop) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      }
    },

    sideNavClick(params) {
      const { back, forward } = params
      if (back) this.goPrev()
      if (forward) this.goNext()
    },
  },
}
</script>

<style name="mobile" scoped>
.footer {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.footer__link {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.footer__link:hover {
  .link__icon {
    color: var(--c-sanitas-green);
  }
}

.link__icon {
  font-size: 24px;
}

.footer__button {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 8px;
}

.button__icon {
  font-size: 24px;
  color: var(--c-white);
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .footer {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0;
  }
}
</style>
